import { useState, useEffect } from 'react';
import { Task, AIInsight, TokenUsage, BillingInfo, AIUsageStats } from '../types';
import { getCustomPriority, getTaskSections } from '../utils/taskUtils';

// GPT-4.1-mini pricing (as of 2024)
const GPT_4_1_MINI_PRICING = {
  input: 0.40 / 1000000, // $0.40 per 1M input tokens
  cached_input: 0.10 / 1000000, // $0.10 per 1M cached input tokens
  output: 1.60 / 1000000, // $1.60 per 1M output tokens
};

export const useAI = () => {
  const [openaiApiKey, setOpenaiApiKey] = useState<string>('');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [aiInsights, setAiInsights] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [currentInsight, setCurrentInsight] = useState<AIInsight | null>(null);
  const [usageStats, setUsageStats] = useState<AIUsageStats>({
    total_sessions: 0,
    total_tokens: 0,
    total_cost: 0,
    current_month_cost: 0,
    insights: []
  });

  // Load data from localStorage on mount
  useEffect(() => {
    const storedApiKey = localStorage.getItem('openai_api_key');
    if (storedApiKey) {
      setOpenaiApiKey(storedApiKey);
    }

    const storedInsights = localStorage.getItem('ai_insights');
    if (storedInsights) {
      try {
        const parsedInsights = JSON.parse(storedInsights);
        if (parsedInsights.length > 0) {
          const lastInsight = parsedInsights[parsedInsights.length - 1];
          setCurrentInsight(lastInsight);
          setAiInsights(lastInsight.content);
        }
      } catch (error) {
        console.error('Error parsing stored insights:', error);
      }
    }

    const storedUsageStats = localStorage.getItem('ai_usage_stats');
    if (storedUsageStats) {
      try {
        setUsageStats(JSON.parse(storedUsageStats));
      } catch (error) {
        console.error('Error parsing usage stats:', error);
      }
    }
  }, []);

  // Save insights to localStorage whenever they change
  useEffect(() => {
    if (usageStats.insights.length > 0) {
      localStorage.setItem('ai_insights', JSON.stringify(usageStats.insights));
      localStorage.setItem('ai_usage_stats', JSON.stringify(usageStats));
    }
  }, [usageStats]);

  const calculateCost = (tokenUsage: TokenUsage): BillingInfo => {
    const inputCost = (tokenUsage.prompt_tokens * GPT_4_1_MINI_PRICING.input);
    const outputCost = (tokenUsage.completion_tokens * GPT_4_1_MINI_PRICING.output);
    const totalCost = inputCost + outputCost;

    return {
      cost_per_1k_input_tokens: GPT_4_1_MINI_PRICING.input * 1000000,
      cost_per_1k_output_tokens: GPT_4_1_MINI_PRICING.output * 1000000,
      total_cost: totalCost,
      model: 'gpt-4.1-mini'
    };
  };

  const getCurrentMonthCost = (insights: AIInsight[]): number => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return insights
      .filter(insight => {
        const insightDate = new Date(insight.created_at);
        return insightDate.getMonth() === currentMonth && insightDate.getFullYear() === currentYear;
      })
      .reduce((total, insight) => total + insight.billing_info.total_cost, 0);
  };

  const saveApiKey = (apiKey: string) => {
    setOpenaiApiKey(apiKey);
    localStorage.setItem('openai_api_key', apiKey);
    setShowApiKeyModal(false);
  };

  const analyzeTasksWithAI = async (tasks: Task[]) => {
    if (!openaiApiKey) {
      setShowApiKeyModal(true);
      return;
    }

    setAiLoading(true);
    try {
      const currentDate = new Date();
      const prompt = `As an AI productivity assistant, analyze these tasks and provide actionable insights. 

**IMPORTANT: Today's date is ${currentDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })} (${currentDate.toLocaleDateString()}). Use this to accurately assess deadlines and time-sensitive tasks.**

Focus on:

1. **Priority Recommendations**: Which tasks should be tackled first and why?
2. **Deadline Risk Assessment**: Any tasks at risk of missing deadlines? Be specific about how many days until deadline.
3. **Workload Analysis**: Is the workload balanced or overwhelming?
4. **Focus Areas**: What should be the main focus for this week?
5. **Potential Blockers**: Any tasks that might block others?

Tasks:
${tasks.map(task => `
- **${task.name}**
  - Project: ${task.projects?.map(p => p.name).join(', ') || 'No project'}
  - Priority: ${getCustomPriority(task) || 'No priority'}
  - Due Date: ${task.due_on ? new Date(task.due_on).toLocaleDateString() : 'No due date'}
  - Created: ${new Date(task.created_at).toLocaleDateString()}
  - Tags: ${task.tags.join(', ') || 'No tags'}
  - Section: ${getTaskSections(task) || 'No section'}
`).join('\n')}

Please provide specific, actionable recommendations in a clear, organized format using markdown formatting.`;

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4.1-mini',
          messages: [
            {
              role: 'system',
              content: 'You are a helpful productivity assistant that analyzes tasks and provides actionable insights. Use markdown formatting for clear, structured responses. When presenting data about tasks (like priorities, deadlines, or comparisons), use markdown tables for better readability.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 1500,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      const tokenUsage: TokenUsage = data.usage;
      const billingInfo = calculateCost(tokenUsage);

      // Create new insight
      const newInsight: AIInsight = {
        id: `insight-${Date.now()}`,
        content,
        created_at: new Date().toISOString(),
        token_usage: tokenUsage,
        billing_info: billingInfo,
        task_count: tasks.length
      };

      // Update usage stats
      const newInsights = [...usageStats.insights, newInsight];
      const newUsageStats: AIUsageStats = {
        total_sessions: usageStats.total_sessions + 1,
        total_tokens: usageStats.total_tokens + tokenUsage.total_tokens,
        total_cost: usageStats.total_cost + billingInfo.total_cost,
        current_month_cost: getCurrentMonthCost(newInsights),
        insights: newInsights
      };

      setUsageStats(newUsageStats);
      setCurrentInsight(newInsight);
      setAiInsights(content);

    } catch (error) {
      console.error('Error analyzing tasks with AI:', error);
      alert('Error analyzing tasks. Please check your API key and try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const clearInsights = () => {
    setAiInsights(null);
    setCurrentInsight(null);
  };

  const clearAllData = () => {
    setUsageStats({
      total_sessions: 0,
      total_tokens: 0,
      total_cost: 0,
      current_month_cost: 0,
      insights: []
    });
    setCurrentInsight(null);
    setAiInsights(null);
    localStorage.removeItem('ai_insights');
    localStorage.removeItem('ai_usage_stats');
  };

  return {
    openaiApiKey,
    showApiKeyModal,
    aiInsights,
    aiLoading,
    currentInsight,
    usageStats,
    setOpenaiApiKey,
    setShowApiKeyModal,
    setAiInsights,
    saveApiKey,
    analyzeTasksWithAI,
    clearInsights,
    clearAllData,
  };
}; 